import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { navigate, replace } from 'components/Navigation/navigationRef';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { format } from 'date-fns';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';
import { intersection, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { doAssetOpenOverview } from 'store/actions';
import {
  CREATE_ASSESSMENT_STEP1,
  LOAD_ASSESSMENT,
} from 'store/assessments/actions';
import { selectAsset } from 'store/assets/selectors';
import {
  selectUserOrganisationFeatures,
  selectUserOrganisationUuidToUid,
} from 'store/auth/selectors';
import {
  selectOrganisation,
  selectOrganisationOrderedFieldSections,
} from 'store/organisations/selectors';

import { filterArrayEmpty } from 'lib/riskbase';
import { UID } from 'types/types';
import { AssetExplorerSummaryRow } from './AssetExplorerSummaryRow';
import { PageHeader } from 'components/PageHeader';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { theme } from 'lib/theme';
import { Gap } from 'components/Gap';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSET],
  Screens.ASSET_EXPLORER
>;

type AssessmentRow = {
  label: string;
  assessmentRow: {
    assetId: UID;
    complianceTypeId: string;
    label: string;
    value: string | undefined;
  };
  continueAssessmentRows: {
    assessmentId: string;
    label: string;
    value?: string;
    assetId: string;
    color: string;
    templateName: string;
  }[];
  createAssessmentRows: {
    assetId: string;
    color: string;
    complianceSubtypeId: string;
    complianceTypeId: string;
    label: string;
    value?: string;
  }[];
};

export const AssetExplorer: React.FC<Props> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const { assetId } = route.params;

  // Compare all details we use in the explorer so it doesn't re-render
  // unnecessarily - alternatively, could use multiple cached selectors
  const asset = useSelector(
    (state) => selectAsset(state, assetId),
    (a, b) => {
      return (
        a.details.name === b.details.name &&
        isEqual(a.compliance_summary, b.compliance_summary) &&
        isEqual(a.assessmentTypesVisible, b.assessmentTypesVisible) &&
        isEqual(
          Object.values(a.assessments).map((ase) => ase?.details),
          Object.values(b.assessments).map((ase) => ase?.details),
        )
      );
    },
  )!;

  const organisation = useSelector((state) =>
    selectOrganisation(state, asset.organisation_id),
  )!;

  const organisationUid = useSelector((state) =>
    selectUserOrganisationUuidToUid(state, organisation.id),
  );

  const userFeatures = useSelector((state) =>
    selectUserOrganisationFeatures(state, organisationUid),
  );

  const fieldSections = useSelector((state) =>
    selectOrganisationOrderedFieldSections(state, organisation.id),
  );

  const assessmentRows = Object.values(organisation.compliance_types)
    .sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    })
    .reduce<AssessmentRow[]>((acc, { id: complianceTypeId }) => {
      const complianceSubtypes = Object.values(
        organisation.compliance_subtypes,
      ).filter((subtype) => subtype.compliance_type_id === complianceTypeId);

      // Skip compliance as no assessment types visible to this asset
      if (
        asset.assessmentTypesVisible &&
        !intersection(
          complianceSubtypes.map(({ id }) => id),
          asset.assessmentTypesVisible,
        ).length
      ) {
        return acc;
      }

      const complianceType = organisation.compliance_types[complianceTypeId]!;
      acc.push({
        label: complianceType.name,
        assessmentRow: {
          assetId,
          complianceTypeId,
          label: `${complianceType.name} Summary`,
          value: complianceSubtypes.some(
            (subtype) => asset.compliance_summary[subtype.id]?.assigned,
          )
            ? "You're Assigned"
            : undefined,
        },
        continueAssessmentRows: complianceSubtypes.reduce<
          AssessmentRow['continueAssessmentRows']
        >((acc1, subtype) => {
          Object.values(asset.assessments).forEach((assessment) => {
            if (
              assessment &&
              assessment.complianceId === subtype.id &&
              (!asset.assessmentTypesVisible ||
                asset.assessmentTypesVisible.includes(subtype.id)) &&
              assessment.editable
            ) {
              const dueDate = asset.compliance_summary[subtype.id]?.due_date;
              const templateName =
                organisation.assessmentTemplates[assessment.templateId]?.name ||
                subtype.name;
              const templateTitle =
                organisation.assessmentTemplates[assessment.templateId]
                  ?.title ||
                assessment.details.title ||
                subtype.name;
              const finalTemplateName = [
                templateName !== subtype.name ? templateName : '',
                templateTitle !== subtype.name && templateTitle !== templateName
                  ? templateTitle
                  : '',
              ]
                .filter(filterArrayEmpty)
                .join('\n');

              acc1.push({
                assessmentId: assessment.id,
                assetId,
                color: `#${
                  organisation.assessment_statuses[assessment.details.statusId]!
                    .colour
                }`,
                label: `Continue ${subtype.name}`,
                value: dueDate
                  ? `Due ${format(new Date(dueDate), UI_DATE_FORMAT_LONG)}`
                  : undefined,
                templateName: finalTemplateName,
              });
            }
          });
          return acc1;
        }, []),
        createAssessmentRows: complianceSubtypes.reduce<
          AssessmentRow['createAssessmentRows']
        >((acc1, subtype) => {
          // If user isn't assigned, don't show create row
          if (!asset.compliance_summary[subtype.id]?.assigned) {
            return acc1;
          }

          // Assessment type isn't visible
          if (
            asset.assessmentTypesVisible &&
            !asset.assessmentTypesVisible.includes(subtype.id)
          ) {
            return acc1;
          }

          // If an assessment is in progress, don't show create row
          if (
            Object.values(asset.assessments).some(
              (assessment) =>
                assessment &&
                assessment.complianceId === subtype.id &&
                assessment.editable,
            )
          ) {
            return acc1;
          }

          // If no template exists, don't show create row
          const templateCount = Object.values(
            organisation.assessmentTemplates,
          ).filter(
            (tpl) => tpl.compliance_subtype_id === subtype.id && !tpl.deleted,
          ).length;
          if (!templateCount) {
            return acc1;
          }

          const dueDate = asset.compliance_summary[subtype.id]?.due_date;
          acc1.push({
            assetId,
            color: `#${organisation.assessment_statuses.notstarted!.colour}`,
            complianceSubtypeId: subtype.id,
            complianceTypeId,
            label: `Create ${subtype.name}`,
            value: `${
              dueDate
                ? `Due ${format(new Date(dueDate), UI_DATE_FORMAT_LONG)}`
                : undefined
            } • You're assigned`,
          });
          return acc1;
        }, []),
      });
      return acc;
    }, []);

  const fieldSectionRows = userFeatures.viewassetfields
    ? fieldSections.map((section) => ({
        groups: section.groupIds.map((groupId) => {
          const group = section.groups[groupId]!;
          return {
            assetId,
            id: group.id,
            label: group.name,
            repeatable: group.repeatable,
            sectionId: section.id,
          };
        }),
        id: section.id,
        name: section.name,
      }))
    : [];

  const handleNavigation = screenSize === 'large' ? replace : navigate;

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Property"
          onPressBackButton={navigation.goBack}
          showSidebarButton
        />
      }
    >
      <ViewWithSeparators separatorMargin={theme.padding * 2}>
        <Card>
          <CardRow
            onPress={() => dispatch(doAssetOpenOverview({ assetId }))}
            navigationIsSelected={{
              name: Screens.ASSET_SUMMARY,
              params: { assetId },
            }}
          >
            <AssetExplorerSummaryRow assetId={assetId} />
          </CardRow>
        </Card>
        {!!assessmentRows.length && (
          <Gap>
            {assessmentRows.map(
              ({
                assessmentRow,
                continueAssessmentRows,
                createAssessmentRows,
                label,
              }) => (
                <Card
                  key={`compliance-overview-${assessmentRow.complianceTypeId}`}
                  label={label}
                >
                  <CardRow
                    label={assessmentRow.label}
                    icon={ICONS.CertificateLight}
                    onPress={() =>
                      handleNavigation(Stacks.ASSET, Screens.ASSET_COMPLIANCE, {
                        assetId: assessmentRow.assetId,
                        complianceTypeId: assessmentRow.complianceTypeId,
                      })
                    }
                    navigationIsSelected={{
                      name: Screens.ASSET_COMPLIANCE,
                      params: {
                        assetId: assessmentRow.assetId,
                        complianceTypeId: assessmentRow.complianceTypeId,
                      },
                    }}
                  />
                  {continueAssessmentRows.map((row) => (
                    <CardRow
                      key={row.assessmentId}
                      labelAlignTop
                      label={row.label}
                      value={row.value}
                      label2={row.templateName}
                      icon={ICONS.FileDuotone}
                      iconColor={row.color}
                      navigationType="modal"
                      onPress={() =>
                        dispatch(
                          LOAD_ASSESSMENT.request({
                            assessmentId: row.assessmentId,
                            assetId: row.assetId,
                          }),
                        )
                      }
                    />
                  ))}
                  {createAssessmentRows.map((row) => (
                    <CardRow
                      key={row.complianceSubtypeId}
                      labelAlignTop
                      label={row.label}
                      value={row.value}
                      icon={ICONS.PlusLight}
                      iconColor={row.color}
                      onPress={() =>
                        dispatch(
                          CREATE_ASSESSMENT_STEP1({
                            assetId: row.assetId,
                            complianceSubtypeId: row.complianceSubtypeId,
                          }),
                        )
                      }
                    />
                  ))}
                </Card>
              ),
            )}
          </Gap>
        )}
        {!!fieldSectionRows.length && (
          <Gap>
            {fieldSectionRows.map((section) => (
              <Card key={section.id} label={section.name}>
                {section.groups.map((group) => (
                  <CardRow
                    key={group.id}
                    label={group.label}
                    onPress={() =>
                      handleNavigation(
                        Stacks.ASSET,
                        group.repeatable
                          ? Screens.ASSET_FIELDREPEATABLE
                          : Screens.ASSET_FIELDSTANDARD,
                        {
                          assetId: group.assetId,
                          groupId: group.id,
                          sectionId: group.sectionId,
                        },
                      )
                    }
                    navigationIsSelected={{
                      name: group.repeatable
                        ? Screens.ASSET_FIELDREPEATABLE
                        : Screens.ASSET_FIELDSTANDARD,
                      params: {
                        assetId: group.assetId,
                        groupId: group.id,
                        sectionId: group.sectionId,
                      },
                    }}
                  />
                ))}
              </Card>
            ))}
          </Gap>
        )}
      </ViewWithSeparators>
    </Page>
  );
};
